import type { Pinia } from 'pinia'
import { useIspInstallStore } from '@/stores/ispInstall'
import type { CommunityItem, GetCommunitiesRequest } from '@/api/rxjs/knst/ISPForm/models/get_communities.model'
import type { AddOnItem } from '@/api/rxjs/knst/ISPForm/models/get_addon_items.model'
import type { ISPPlanDetail, ISPPlanType } from '@/api/rxjs/knst/ISPForm/models/get_plans.model'
import type { GetRoadCommunityItem } from '@/api/rxjs/knst/ISPForm/models/get_road_community.model'
import type { ISP_SUBMIT_MODE } from '@/views/ISPInstall/pages/step/enums/ISPSubmitMode.enum'
import type { COMMUNITY_QUERY_RESULT } from '@/views/ISPInstall/pages/step/enums/CommunityQueryResult.enum'
import { SelectedPlan } from '@/views/ISPInstall/pages/step/models/selected_plan.model'
import type { ISPTypeForm } from '@/views/ISPInstall/pages/step/models/isp_type_form.model'
import type { ISPInfoForm } from '@/views/ISPInstall/pages/step/models/isp_info_form.model'

const STORAGE_KEY = 'ispInstall:flow'

/**
 * 持久化到 sessionStorage 的流程狀態（排除 validateArray 函式、pageLoading）。
 *
 * 刻意不持久化 planData / defaultPlanData：那是後台（admin_plans）隨時可改的伺服器主資料，
 * 一旦寫進 sessionStorage，重整時 hydrate 會把舊清單塞回 store，讀取端的
 * 「有值就不打 API」判斷便永遠短路，後台改完方案在同一分頁怎麼硬重整都看不到更新
 * （Ctrl+Shift+R 不清 sessionStorage），得關掉分頁才會生效。方案清單一律每次進頁重抓。
 */
interface PersistedFlow {
  bannerToggler?: boolean
  userSource?: number
  searchNPick_queryForm?: Partial<GetCommunitiesRequest>
  searchNPick_communityList?: CommunityItem[]
  searchNPick_userInputForm?: { communityName: string }
  searchNPick_queryStatus?: COMMUNITY_QUERY_RESULT
  searchNPick_ispForm?: Partial<ISPTypeForm>
  searchNPick_districtOptions?: string[]
  searchNPick_roadOptions?: string[]
  searchNPick_selectedRoad?: string
  searchNPick_communityOptions?: GetRoadCommunityItem[]
  searchNPick_selectedCommunityName?: string
  selectedPlan?: { planType: ISPPlanType | null, planDetail: ISPPlanDetail | null, addonItems: Record<number, number> }
  groupBuy_selectedId?: number | null
  groupBuy_originalPlan?: { planType: ISPPlanType | null, planDetail: ISPPlanDetail | null } | null
  selectedCommunity?: CommunityItem | null
  infoForm?: Partial<ISPInfoForm>
  submitMode?: ISP_SUBMIT_MODE
  consultMode?: boolean
  addonItemList?: AddOnItem[]
  addonIdPriceMap?: Array<[number, number]>
}

type IspInstallStore = ReturnType<typeof useIspInstallStore>

function serialize(store: IspInstallStore): string {
  const sp = store.selectedPlan
  const payload: PersistedFlow = {
    bannerToggler: store.bannerToggler,
    userSource: store.userSource,
    searchNPick_queryForm: store.searchNPick_queryForm,
    searchNPick_communityList: store.searchNPick_communityList,
    searchNPick_userInputForm: store.searchNPick_userInputForm,
    searchNPick_queryStatus: store.searchNPick_queryStatus,
    searchNPick_ispForm: store.searchNPick_ispForm,
    searchNPick_districtOptions: store.searchNPick_districtOptions,
    searchNPick_roadOptions: store.searchNPick_roadOptions,
    searchNPick_selectedRoad: store.searchNPick_selectedRoad,
    searchNPick_communityOptions: store.searchNPick_communityOptions,
    searchNPick_selectedCommunityName: store.searchNPick_selectedCommunityName,
    selectedPlan: { planType: sp.planType, planDetail: sp.planDetail, addonItems: sp.addonItems },
    groupBuy_selectedId: store.groupBuy_selectedId,
    groupBuy_originalPlan: store.groupBuy_originalPlan,
    selectedCommunity: store.selectedCommunity,
    infoForm: store.infoForm,
    submitMode: store.submitMode,
    consultMode: store.consultMode,
    addonItemList: store.addonItemList,
    addonIdPriceMap: Array.from(store.addonIdPriceMap.entries())
  }
  return JSON.stringify(payload)
}

function parseFlow(raw: string): PersistedFlow | null {
  try {
    return JSON.parse(raw) as PersistedFlow
  } catch {
    // sessionStorage 內容毀損 → 忽略、保持初始狀態
    return null
  }
}

/**
 * 還原 SelectedPlan 並重建加購數量的 computed ref。
 * 注意：computed 的 getter/setter 閉包會捕捉 `this`，必須在「已掛進 store（reactive proxy）」
 * 的實例上呼叫 setAddonSetterGetter，否則 computed 綁到 raw 物件 → el-input-number 讀不到追蹤、
 * 寫入也不觸發重繪（加購 +/- 看起來沒反應）。故先 assign 再建 ref。
 */
function hydrateSelectedPlan(store: IspInstallStore, data: PersistedFlow['selectedPlan']): void {
  const sp = new SelectedPlan()
  sp.planType = data?.planType ?? null
  sp.planDetail = data?.planDetail ?? null
  store.selectedPlan = sp
  const items = data?.addonItems ?? {}
  Object.keys(items).forEach(key => {
    const id = Number(key)
    store.selectedPlan.setAddonSetterGetter(id)
    store.selectedPlan.addonItems[id] = items[id]
  })
}

function hydrate(store: IspInstallStore): void {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return
  const d = parseFlow(raw)
  if (!d) return

  store.bannerToggler = d.bannerToggler ?? true
  store.userSource = d.userSource ?? 0
  if (d.searchNPick_queryForm) Object.assign(store.searchNPick_queryForm, d.searchNPick_queryForm)
  store.searchNPick_communityList = d.searchNPick_communityList ?? []
  store.searchNPick_userInputForm = d.searchNPick_userInputForm ?? { communityName: '' }
  if (d.searchNPick_queryStatus !== undefined) store.searchNPick_queryStatus = d.searchNPick_queryStatus
  if (d.searchNPick_ispForm) Object.assign(store.searchNPick_ispForm, d.searchNPick_ispForm)
  store.searchNPick_districtOptions = d.searchNPick_districtOptions ?? []
  store.searchNPick_roadOptions = d.searchNPick_roadOptions ?? []
  store.searchNPick_selectedRoad = d.searchNPick_selectedRoad ?? ''
  store.searchNPick_communityOptions = d.searchNPick_communityOptions ?? []
  store.searchNPick_selectedCommunityName = d.searchNPick_selectedCommunityName ?? ''
  hydrateSelectedPlan(store, d.selectedPlan)
  store.groupBuy_selectedId = d.groupBuy_selectedId ?? null
  store.groupBuy_originalPlan = d.groupBuy_originalPlan ?? null
  store.selectedCommunity = d.selectedCommunity ?? null
  if (d.infoForm) Object.assign(store.infoForm, d.infoForm)
  // planData / defaultPlanData 不還原（見 PersistedFlow 註解），由各頁 onMounted 重抓
  if (d.submitMode !== undefined) store.submitMode = d.submitMode
  store.consultMode = d.consultMode ?? false
  store.addonItemList = d.addonItemList ?? []
  store.addonIdPriceMap = new Map(d.addonIdPriceMap ?? [])
}

/** 清除已持久化的流程狀態（如流程送出後可呼叫） */
export function clearIspInstallPersist(): void {
  sessionStorage.removeItem(STORAGE_KEY)
}

/** App 啟動時呼叫：先還原 sessionStorage，再訂閱後續變更寫回（重整不掉資料、關分頁即清）*/
export function setupIspInstallPersist(pinia: Pinia): void {
  const store = useIspInstallStore(pinia)

  // 1. 先還原（在訂閱之前，避免還原賦值又觸發寫入）
  hydrate(store)

  // 2. 訂閱變更 → 寫回 sessionStorage
  store.$subscribe(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, serialize(store))
    } catch {
      // 寫入失敗（如容量上限）→ 忽略，不影響流程
    }
  })
}
